/**
 * نمایش متن مقاله
 * ================
 * مقاله‌ها با نشانه‌گذاری ساده (Markdown) نوشته می‌شوند و اینجا به عناصر React
 * تبدیل می‌شوند — نه با `dangerouslySetInnerHTML`.
 *
 * دلیلش امنیت است: اگر روزی حساب یک نویسنده لو برود، متن مقاله نباید بتواند
 * اسکریپت اجرا کند. در صرافی، اسکریپت تزریق‌شده در صفحه‌ای که کاربرِ وارد‌شده
 * می‌بیند یعنی امکان سرقت توکن نشست.
 *
 * لنگر تیترها دقیقاً با همان قاعده بک‌اند ساخته می‌شود، وگرنه لینک‌های فهرست
 * مطالب به جایی نمی‌رسند.
 */
import { Fragment, ReactNode } from 'react'
import { Link } from 'react-router-dom'

const STOP_WORDS = new Set([
  'و', 'در', 'به', 'از', 'با', 'که', 'را', 'این', 'آن', 'برای',
  'تا', 'یا', 'هم', 'است', 'بر', 'می', 'های', 'ها',
])

/**
 * همتای `slugify` در بک‌اند (`services/blog.py`) — باید عیناً همان خروجی را بدهد.
 *
 * اگر این دو از هم فاصله بگیرند، لینک‌های فهرست مطالب به تیترها نمی‌رسند و
 * کاربر روی فهرست کلیک می‌کند و هیچ اتفاقی نمی‌افتد. هر تغییر باید در هر دو
 * فایل انجام شود.
 */
export function slugify(text: string, maxWords = 6): string {
  let t = String(text || '').trim()
  t = t.replace(/‌/g, '-').replace(/_/g, '-')
  t = t.replace(/[^0-9A-Za-zء-غف-يٮ-ۓ۰-۹\-]+/g, '-')
  const words = t.split('-').filter((w) => w && !STOP_WORDS.has(w))
  let slug = words.slice(0, maxWords).join('-').replace(/^-+|-+$/g, '').toLowerCase()
  slug = slug.replace(/-{2,}/g, '-')
  return slug.slice(0, 180)
}

/** استخراج تیترها با همان شماره‌گذاری تکراری‌های بک‌اند. */
export function tocOf(body: string): { level: number; text: string; id: string }[] {
  const out: { level: number; text: string; id: string }[] = []
  const used: Record<string, number> = {}
  const lines = String(body || '').split('\n')
  for (const line of lines) {
    const m = /^(#{2,3})\s+(.+)$/.exec(line.trim())
    if (!m) continue
    const text = m[2].replace(/[*_`]/g, '').trim()
    if (!text) continue
    let anchor = slugify(text) || `h${out.length + 1}`
    if (used[anchor]) {
      used[anchor] += 1
      anchor = `${anchor}-${used[anchor]}`
    } else {
      used[anchor] = 1
    }
    out.push({ level: m[1].length, text: text.slice(0, 120), id: anchor })
  }
  return out
}

/** متن درون‌خطی: پررنگ، مورب، کد، لینک و تصویر. */
function inline(text: string, keyBase: string): ReactNode[] {
  const nodes: ReactNode[] = []
  // تصویر (!\[…\]) باید **پیش از** لینک بیاید، وگرنه بخش [alt](url) به‌عنوان
  // لینک گرفته می‌شود و یک «!» سرگردان در متن باقی می‌ماند.
  const re = /(!\[[^\]]*\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index))
    const tok = m[0]
    const key = `${keyBase}-i${i++}`
    if (tok.startsWith('![')) {
      const cut = tok.indexOf('](')
      const alt = tok.slice(2, cut)
      const src = tok.slice(cut + 2, -1)
      nodes.push(
        <img key={key} src={src} alt={alt} loading="lazy" decoding="async"
             className="my-2 inline-block max-w-full rounded-lg border border-white/10 align-middle" />,
      )
    } else if (tok.startsWith('**')) {
      nodes.push(<strong key={key} className="font-bold text-white">{tok.slice(2, -2)}</strong>)
    } else if (tok.startsWith('`')) {
      nodes.push(
        <code key={key} className="rounded bg-white/[.07] px-1.5 py-0.5 font-mono text-[13px] text-aqua">
          {tok.slice(1, -1)}
        </code>,
      )
    } else if (tok.startsWith('[')) {
      const cut = tok.indexOf('](')
      const label = tok.slice(1, cut)
      const href = tok.slice(cut + 2, -1)
      // لینک داخلی با روتر باز می‌شود (بدون بارگذاری دوباره صفحه)؛
      // لینک بیرونی با rel امن تا اعتبار سئوی سایت به آن منتقل نشود.
      nodes.push(
        /^https?:\/\//i.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow"
             className="text-brand-300 underline decoration-brand-500/40 underline-offset-4 hover:text-brand-200">
            {label}
          </a>
        ) : (
          <Link key={key} to={href}
                className="text-brand-300 underline decoration-brand-500/40 underline-offset-4 hover:text-brand-200">
            {label}
          </Link>
        ),
      )
    } else {
      nodes.push(<em key={key} className="italic text-slate-200">{tok.slice(1, -1)}</em>)
    }
    last = m.index + tok.length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

export default function Markdown({ body }: { body: string }) {
  const lines = String(body || '').replace(/\r\n/g, '\n').split('\n')
  const out: ReactNode[] = []
  const used: Record<string, number> = {}
  let i = 0
  let k = 0

  const anchor = (text: string) => {
    let a = slugify(text) || `h${k}`
    if (used[a]) {
      used[a] += 1
      a = `${a}-${used[a]}`
    } else {
      used[a] = 1
    }
    return a
  }

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (!trimmed) { i++; continue }

    // بلوک کد
    if (trimmed.startsWith('```')) {
      const buf: string[] = []
      i++
      while (i < lines.length && !lines[i].trim().startsWith('```')) buf.push(lines[i++])
      i++
      out.push(
        <pre key={`k${k++}`} dir="ltr"
             className="overflow-x-auto rounded-2xl border border-white/10 bg-black/40 p-4 text-left font-mono text-[13px] leading-7 text-slate-300">
          <code>{buf.join('\n')}</code>
        </pre>,
      )
      continue
    }

    // تیتر
    const h = /^(#{1,4})\s+(.+)$/.exec(trimmed)
    if (h) {
      const level = h[1].length
      const text = h[2].trim()
      const id = level >= 2 && level <= 3 ? anchor(text.replace(/[*_`]/g, '')) : undefined
      const cls = level <= 1
        ? 'mt-10 mb-4 text-2xl font-extrabold text-white'
        : level === 2
          ? 'mt-10 mb-4 scroll-mt-24 border-r-4 border-brand-500/60 pr-3 text-xl font-extrabold text-white'
          : level === 3
            ? 'mt-8 mb-3 scroll-mt-24 text-lg font-bold text-white'
            : 'mt-6 mb-2 font-bold text-slate-200'
      // «#» به h2 نگاشته می‌شود، نه h1: تیتر خود مقاله همان h1 صفحه است و دو
      // h1 در یک صفحه، ساختار را برای گوگل مبهم می‌کند. بقیه سطح‌ها همان‌طور
      // که نوشته شده‌اند می‌مانند تا سلسله‌مراتب تیترها معنادار بماند.
      const Tag = (`h${Math.min(Math.max(level, 2), 6)}`) as any
      out.push(<Tag key={`k${k++}`} id={id} className={cls}>{inline(text, `h${k}`)}</Tag>)
      i++
      continue
    }

    // خط جداکننده
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      out.push(<hr key={`k${k++}`} className="my-8 border-white/10" />)
      i++
      continue
    }

    // نقل‌قول
    if (trimmed.startsWith('>')) {
      const buf: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        buf.push(lines[i].trim().replace(/^>\s?/, ''))
        i++
      }
      out.push(
        <blockquote key={`k${k++}`}
          className="my-6 rounded-2xl border-r-4 border-brand-500/60 bg-white/[.03] p-4 text-sm leading-8 text-slate-300">
          {inline(buf.join(' '), `q${k}`)}
        </blockquote>,
      )
      continue
    }

    // تصویر تنها در یک خط
    const img = /^!\[([^\]]*)\]\(([^)\s]+)\)$/.exec(trimmed)
    if (img) {
      out.push(
        <figure key={`k${k++}`} className="my-7">
          <img src={img[2]} alt={img[1]} loading="lazy" decoding="async"
               className="w-full rounded-2xl border border-white/10" />
          {img[1] && (
            <figcaption className="mt-2 text-center text-xs text-slate-500">{img[1]}</figcaption>
          )}
        </figure>,
      )
      i++
      continue
    }

    // فهرست
    const bullet = /^[-*+]\s+(.+)$/
    const numbered = /^\d+[.)]\s+(.+)$/
    if (bullet.test(trimmed) || numbered.test(trimmed)) {
      const ordered = numbered.test(trimmed)
      const items: string[] = []
      while (i < lines.length) {
        const t = lines[i].trim()
        const m = ordered ? numbered.exec(t) : bullet.exec(t)
        if (!m) break
        items.push(m[1])
        i++
      }
      const List = ordered ? 'ol' : 'ul'
      out.push(
        <List key={`k${k++}`}
          className={`my-5 space-y-2 pr-6 text-sm leading-8 text-slate-300 ${ordered ? 'list-decimal' : 'list-disc'} marker:text-brand-400`}>
          {items.map((it, n) => <li key={n}>{inline(it, `l${k}-${n}`)}</li>)}
        </List>,
      )
      continue
    }

    // جدول ساده
    if (trimmed.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:-]+\|[\s|:-]*$/.test(lines[i + 1])) {
      const cells = (row: string) =>
        row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim())
      const head = cells(trimmed)
      i += 2
      const body: string[][] = []
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        body.push(cells(lines[i]))
        i++
      }
      out.push(
        <div key={`k${k++}`} className="my-6 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full text-right text-sm">
            <thead className="bg-white/[.04] text-xs text-slate-400">
              <tr>{head.map((c, n) => <th key={n} className="px-4 py-3 font-semibold">{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-300">
              {body.map((row, n) => (
                <tr key={n}>{row.map((c, m) => (
                  <td key={m} className="px-4 py-3 leading-7">{inline(c, `t${k}-${n}-${m}`)}</td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      )
      continue
    }

    // پاراگراف — تا خط خالی بعدی
    const buf: string[] = []
    while (i < lines.length && lines[i].trim() &&
           !/^(#{1,4}\s|```|>|[-*+]\s|\d+[.)]\s)/.test(lines[i].trim())) {
      buf.push(lines[i].trim())
      i++
    }
    if (buf.length) {
      out.push(
        <p key={`k${k++}`} className="my-5 text-[15px] leading-9 text-slate-300">
          {inline(buf.join(' '), `p${k}`)}
        </p>,
      )
    }
  }

  return <Fragment>{out}</Fragment>
}
